import { useLazyQuery } from '@apollo/client'
import { Download, Visibility } from '@mui/icons-material'
import { IconButton, Stack, Tooltip } from '@mui/material'
import { GetResumeUrl } from 'recruiter/graphql/documents.gql'
import { useState } from 'react'
import ResumePreviewDialog, {
    isPdfDocument,
} from 'shared/components/ResumePreviewDialog'

export default function ResumeDownloadButton({
    documentId,
    documentKey,
}: {
    documentId: string
    documentKey?: string | null
}) {
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)

    const [download, { loading }] = useLazyQuery(GetResumeUrl, {
        fetchPolicy: 'network-only',
    })

    const handleDownload = async (e: React.MouseEvent) => {
        e.stopPropagation()
        const { data } = await download({ variables: { documentId } })
        if (data?.download?.url) {
            const link = document.createElement('a')
            link.href = data.download.url
            link.target = '_blank'
            link.click()
            link.remove()
        }
    }

    const handlePreview = async (e: React.MouseEvent) => {
        e.stopPropagation()
        setPreviewUrl(null)
        setPreviewLoading(true)
        setPreviewOpen(true)
        try {
            const { data } = await download({
                variables: { documentId, forceInline: true },
            })
            setPreviewUrl(data?.download?.url ?? null)
        } catch {
            setPreviewUrl(null)
        }
        setPreviewLoading(false)
    }

    const canPreview = isPdfDocument(documentKey)
    const fileName = documentKey?.split('/').pop() || undefined

    return (
        <>
            <Stack direction="row" spacing={0}>
                {canPreview && (
                    <Tooltip title="Preview">
                        <IconButton
                            size="small"
                            onClick={handlePreview}
                            disabled={loading}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Download">
                    <IconButton
                        size="small"
                        onClick={handleDownload}
                        disabled={loading}
                    >
                        <Download fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <ResumePreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                url={previewUrl}
                fileName={fileName}
                loading={previewLoading}
            />
        </>
    )
}
